// Timestamp: a fixed point in time, stored as an ISO-8601 UTC string (always
// `...Z`, from `new Date().toISOString()`). This is the canonical form for every
// stored, queued, compared, and wire-transmitted time value. Branded so a plain
// string can't stand in for a validated timestamp; the brand is compile-time
// only — erased at runtime, so the value survives structured clone (IndexedDB)
// and JSON as an ordinary string.
//
// Local time is a display concern only: convert to local at the UI boundary
// (usually as a separate date and time), never store or compare local values.

declare const brand: unique symbol

export type Timestamp = string & { readonly [brand]: 'Timestamp' }

/** The current moment as a Timestamp. */
export const timestampNow = (): Timestamp => new Date().toISOString() as Timestamp

/** Tag a Date as a Timestamp. */
export const timestampFromDate = (date: Date): Timestamp => date.toISOString() as Timestamp

/** A Timestamp back to a Date. */
export const timestampToDate = (timestamp: Timestamp): Date => new Date(timestamp)

/**
 * Validate and tag an arbitrary string as a Timestamp. Returns null if it does
 * not round-trip through Date — the single choke point for "is this really a
 * valid timestamp?".
 */
export function parseTimestamp(value: string): Timestamp | null {
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : timestampFromDate(parsed)
}
